import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ShieldCheck, Users, HeartHandshake, Calendar, Quote } from "lucide-react";
import { IMG, BTN, BRAND } from "@/lib/design";
import { fade } from "../_shared/motion";

const HERO_IMAGE_FADE = { ...fade.transition, delay: 0.15 };

export default function HeroSection() {
    return (
        <section className="relative overflow-hidden bg-[#F7F5F0]">
            <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-14 pt-14 md:pt-20 pb-20 md:pb-28 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
                <motion.div {...fade} className="lg:col-span-6 relative z-10">
                    <img
                        src={BRAND.logo}
                        alt="Crossroads to Change Residential Treatment Center"
                        className="h-20 sm:h-24 w-auto mb-6 drop-shadow-md"
                        data-testid="hero-logo-img"
                    />
                    <span className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white border border-[#E5DFD3] text-[12px] tracking-wider uppercase text-[#0E2A47] font-medium">
                        <span className="h-1.5 w-1.5 rounded-full bg-[#C8A24A]" />
                        Opening Summer of 2026 · Conveniently Located in Houston / Tomball Area
                    </span>
                    <br />
                    <span
                        className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#C8A24A]/15 border border-[#C8A24A]/40 text-[12px] tracking-wider uppercase text-[#8a6b1f] font-semibold mt-3"
                        data-testid="emergency-placement-badge"
                    >
                        <span className="relative flex h-2 w-2">
                            <span className="absolute inline-flex h-full w-full rounded-full bg-[#C8A24A] opacity-75 animate-ping" />
                            <span className="relative inline-flex h-2 w-2 rounded-full bg-[#C8A24A]" />
                        </span>
                        Accepting Temporary Emergency Placements
                    </span>
                    <h1 className="mt-6 font-[Outfit] text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-[#1A2230] leading-[1.05]">
                        A Safe Place for{" "}
                        <span className="text-[#0E2A47]">Growth, Healing,</span>{" "}
                        and Accountability.
                    </h1>
                    <p className="mt-5 text-[15px] uppercase tracking-[0.18em] text-[#C8A24A] font-semibold">
                        From Trauma to Stability. From Survival to Change.
                    </p>
                    <p className="mt-6 text-[17px] leading-relaxed text-[#6F6B63] max-w-xl">
                        Crossroads to Change Residential Treatment Center
                        is a trauma-informed residential treatment program
                        serving male youth in Texas foster care — providing
                        structure, clinical support, and a safe environment
                        to stabilize, heal, and grow.
                    </p>
                    <div className="mt-9 flex flex-wrap gap-3">
                        <Link to="/contact" className={BTN.primary} data-testid="hero-info-cta">
                            Request Information <ArrowRight className="h-4 w-4" />
                        </Link>
                        <Link to="/referrals" className={BTN.secondary} data-testid="hero-referral-cta">
                            Submit a Referral
                        </Link>
                        <Link to="/contact#schedule" className={BTN.outline} data-testid="hero-consult-cta">
                            <Calendar className="h-4 w-4" />
                            Schedule a Consultation
                        </Link>
                    </div>
                    <div className="mt-10 flex items-center gap-6 text-[13px] text-[#6F6B63]">
                        <div className="flex items-center gap-2">
                            <ShieldCheck className="h-4 w-4 text-[#0E2A47]" />
                            Trauma-Informed
                        </div>
                        <div className="flex items-center gap-2">
                            <Users className="h-4 w-4 text-[#0E2A47]" />
                            TBRI-Informed Care
                        </div>
                        <div className="flex items-center gap-2">
                            <HeartHandshake className="h-4 w-4 text-[#0E2A47]" />
                            24/7 Therapeutic Support
                        </div>
                    </div>
                </motion.div>

                <motion.div
                    {...fade}
                    transition={HERO_IMAGE_FADE}
                    className="lg:col-span-6 relative"
                >
                    <div className="relative aspect-[4/5] rounded-[2rem] overflow-hidden bg-[#EFEBE0]">
                        <img
                            src={IMG.hero}
                            alt="A mentor guiding a young man — every nationality welcome"
                            className="absolute inset-0 w-full h-full object-cover"
                            data-testid="hero-image"
                        />
                        <div className="absolute inset-0 bg-gradient-to-tr from-[#0E2A47]/45 via-[#0E2A47]/10 to-transparent" />
                    </div>
                    <div className="absolute -bottom-6 -left-4 sm:-left-8 max-w-[320px] bg-white rounded-2xl border border-[#E5DFD3] p-5 shadow-lg shadow-[#0E2A47]/10">
                        <Quote className="h-5 w-5 text-[#C8A24A] mb-2" />
                        <p className="font-[Outfit] text-[14.5px] text-[#1A2230] leading-snug" data-testid="hero-ceo-quote">
                            &ldquo;Empowering young men in foster care to break cycles of trauma, build healthy relationships, and develop the confidence and skills needed for a successful future.&rdquo;
                        </p>
                        <div className="mt-4 flex items-center gap-2.5">
                            <span className="h-8 w-8 rounded-full bg-[#0E2A47] grid place-items-center text-white font-[Outfit] font-semibold text-[11px]">
                                DS
                            </span>
                            <div className="leading-tight">
                                <p className="text-[12.5px] font-medium text-[#1A2230]">
                                    Dorothy Summage &amp; Valerie Payton
                                </p>
                                <p className="text-[10.5px] text-[#6F6B63] uppercase tracking-[0.12em] mt-0.5">
                                    Founder &amp; CEO
                                </p>
                            </div>
                        </div>
                    </div>
                </motion.div>
            </div>
        </section>
    );
}
